import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { PageHeader, ScopeNotice, useScopeGuard } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Truck, Wheat, Package, Users, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import CertsEditor from "@/components/CertsEditor";
import ImportDialog from "@/components/ImportDialog";

const EMPTY = {
  proveedores: { nombre: "", cif: "", contacto: "", certificaciones: [] },
  clientes: { nombre: "", cif: "", persona_contacto: "", email: "", telefono: "", direccion: "", poblacion: "", pais: "España", notas: "", certificaciones: [] },
  "materias-primas": { nombre: "", codigo: "", alergenos: "", proveedor_id: "", unidad: "kg" },
  productos: { nombre: "", codigo: "", alergenos: "", unidad: "kg", rendimiento_esperado_pct: "", tolerancia_pct: "" },
};

export default function Traza() {
  const { user, scopeTenant } = useAuth();
  const canEdit = ["superadmin", "admin", "calidad"].includes(user.rol);
  const needsScope = useScopeGuard(user, scopeTenant);

  const [prov, setProv] = useState([]);
  const [mp, setMp] = useState([]);
  const [prod, setProd] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [importEntidad, setImportEntidad] = useState(null);

  const [dialog, setDialog] = useState(null); // { tipo, editId }
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    if (needsScope) return;
    const [p, m, pr, c] = await Promise.all([
      api.get("/proveedores"), api.get("/materias-primas"), api.get("/productos"), api.get("/clientes"),
    ]);
    setProv(p.data); setMp(m.data); setProd(pr.data); setClientes(c.data);
  }, [needsScope]);
  useEffect(() => { load(); }, [load]);

  const abrir = (tipo, item = null) => {
    if (item) {
      const base = { ...item, certificaciones: item.certificaciones || [] };
      if (base.alergenos) base.alergenos = base.alergenos.join(", ");
      setForm(base);
      setDialog({ tipo, editId: item.id });
    } else {
      setForm({ ...EMPTY[tipo], certificaciones: [] });
      setDialog({ tipo, editId: null });
    }
  };

  const guardar = async () => {
    const { tipo, editId } = dialog;
    if (!form.nombre?.trim()) return toast.error("El nombre es obligatorio");
    const payload = { ...form };
    delete payload.id; delete payload.tenant_id; delete payload.activo; delete payload.creado_en;
    if (tipo === "materias-primas" || tipo === "productos") {
      payload.alergenos = (payload.alergenos || "").split(",").map((s) => s.trim()).filter(Boolean);
    }
    if (tipo === "productos") {
      payload.rendimiento_esperado_pct = payload.rendimiento_esperado_pct === "" || payload.rendimiento_esperado_pct == null ? null : parseFloat(payload.rendimiento_esperado_pct);
      payload.tolerancia_pct = payload.tolerancia_pct === "" || payload.tolerancia_pct == null ? null : parseFloat(payload.tolerancia_pct);
    }
    if (tipo === "materias-primas" && !payload.proveedor_id) payload.proveedor_id = null;
    try {
      if (editId) await api.patch(`/${tipo}/${editId}`, payload);
      else await api.post(`/${tipo}`, payload);
      toast.success(editId ? "Actualizado" : "Creado");
      setDialog(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (needsScope) return <ScopeNotice />;

  const provNombre = (id) => prov.find((p) => p.id === id)?.nombre || "—";

  const nuevoBtn = (tipo, label) => canEdit && (
    <Button onClick={() => abrir(tipo)} data-testid={`nuevo-${tipo}-btn`} className="bg-[#FF5625] hover:bg-[#E0451A]">
      <Plus size={16} className="mr-1" />{label}
    </Button>
  );

  const acciones = (tipo, label) => canEdit && (
    <div className="flex flex-wrap justify-end gap-2 mb-3">
      <Button variant="outline" onClick={() => setImportEntidad(tipo)} data-testid={`importar-${tipo}-btn`}>
        <FileSpreadsheet size={16} className="mr-1" />Importar
      </Button>
      {nuevoBtn(tipo, label)}
    </div>
  );

  const certResumen = (arr) => {
    const certs = arr || [];
    if (!certs.length) return <span className="text-[#0E2A47]/30">—</span>;
    const prox = certs.filter((c) => c.fecha_caducidad).sort((a, b) => a.fecha_caducidad.localeCompare(b.fecha_caducidad))[0];
    return (
      <div className="flex flex-wrap gap-1 items-center">
        <Badge variant="outline" className="text-[10px]">{certs.length} cert.</Badge>
        {prox && <span className="text-[10px] text-[#0E2A47]/50">próx. {new Date(prox.fecha_caducidad).toLocaleDateString("es-ES")}</span>}
      </div>
    );
  };

  const alergBadges = (arr) => (arr && arr.length ? (
    <div className="flex flex-wrap gap-1">{arr.map((a) => <Badge key={a} variant="outline" className="text-[10px] border-[#F59E0B] text-[#B45309]">{a}</Badge>)}</div>
  ) : <span className="text-[#0E2A47]/30">—</span>);

  return (
    <div data-testid="traza-maestros-page">
      <PageHeader overline="Nivel 3 · Traza" title="Maestros" />

      <Tabs defaultValue="proveedores">
        <TabsList className="bg-white border border-[#E2E8F0]">
          <TabsTrigger value="proveedores" data-testid="tab-proveedores"><Truck size={14} className="mr-1.5" />Proveedores</TabsTrigger>
          <TabsTrigger value="clientes" data-testid="tab-clientes"><Users size={14} className="mr-1.5" />Clientes</TabsTrigger>
          <TabsTrigger value="materias-primas" data-testid="tab-materias-primas"><Wheat size={14} className="mr-1.5" />Materias primas</TabsTrigger>
          <TabsTrigger value="productos" data-testid="tab-productos"><Package size={14} className="mr-1.5" />Productos</TabsTrigger>
        </TabsList>

        {/* Proveedores */}
        <TabsContent value="proveedores">
          {acciones("proveedores", "Proveedor")}
          <Card className="border-[#E2E8F0]">
            <Table data-testid="tabla-proveedores">
              <TableHeader><TableRow>
                <TableHead>Nombre</TableHead><TableHead>CIF</TableHead><TableHead>Contacto</TableHead>
                <TableHead>Certificaciones</TableHead><TableHead className="w-16"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {prov.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-[#0E2A47]/40 py-8">Sin proveedores</TableCell></TableRow>}
                {prov.map((p) => (
                  <TableRow key={p.id} data-testid={`proveedor-${p.id}`}>
                    <TableCell className="font-medium text-[#0E2A47]">{p.nombre}</TableCell>
                    <TableCell>{p.cif || "—"}</TableCell>
                    <TableCell>{p.contacto || "—"}</TableCell>
                    <TableCell>{certResumen(p.certificaciones)}</TableCell>
                    <TableCell>{canEdit && <Button size="sm" variant="ghost" onClick={() => abrir("proveedores", p)} data-testid={`editar-proveedor-${p.id}`}><Pencil size={14} /></Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Clientes */}
        <TabsContent value="clientes">
          {acciones("clientes", "Cliente")}
          <Card className="border-[#E2E8F0]">
            <Table data-testid="tabla-clientes">
              <TableHeader><TableRow>
                <TableHead>Nombre</TableHead><TableHead>Contacto</TableHead><TableHead>Teléfono</TableHead>
                <TableHead>Email</TableHead><TableHead>Certificaciones</TableHead><TableHead className="w-16"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {clientes.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-[#0E2A47]/40 py-8">Sin clientes</TableCell></TableRow>}
                {clientes.map((c) => (
                  <TableRow key={c.id} data-testid={`cliente-${c.id}`}>
                    <TableCell className="font-medium text-[#0E2A47]">{c.nombre}</TableCell>
                    <TableCell>{c.persona_contacto || "—"}</TableCell>
                    <TableCell>{c.telefono || <span className="text-[#EF4444]">⚠ falta</span>}</TableCell>
                    <TableCell>{c.email || <span className="text-[#EF4444]">⚠ falta</span>}</TableCell>
                    <TableCell>{certResumen(c.certificaciones)}</TableCell>
                    <TableCell>{canEdit && <Button size="sm" variant="ghost" onClick={() => abrir("clientes", c)} data-testid={`editar-cliente-${c.id}`}><Pencil size={14} /></Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Materias primas */}
        <TabsContent value="materias-primas">
          {acciones("materias-primas", "Materia prima")}
          <Card className="border-[#E2E8F0]">
            <Table data-testid="tabla-materias-primas">
              <TableHeader><TableRow>
                <TableHead>Nombre</TableHead><TableHead>Código</TableHead><TableHead>Alérgenos</TableHead>
                <TableHead>Proveedor</TableHead><TableHead>Unidad</TableHead><TableHead className="w-16"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {mp.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-[#0E2A47]/40 py-8">Sin materias primas</TableCell></TableRow>}
                {mp.map((m) => (
                  <TableRow key={m.id} data-testid={`materia-prima-${m.id}`}>
                    <TableCell className="font-medium text-[#0E2A47]">{m.nombre}</TableCell>
                    <TableCell>{m.codigo || "—"}</TableCell>
                    <TableCell>{alergBadges(m.alergenos)}</TableCell>
                    <TableCell>{provNombre(m.proveedor_id)}</TableCell>
                    <TableCell>{m.unidad}</TableCell>
                    <TableCell>{canEdit && <Button size="sm" variant="ghost" onClick={() => abrir("materias-primas", m)} data-testid={`editar-materia-prima-${m.id}`}><Pencil size={14} /></Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Productos */}
        <TabsContent value="productos">
          {acciones("productos", "Producto")}
          <Card className="border-[#E2E8F0]">
            <Table data-testid="tabla-productos">
              <TableHeader><TableRow>
                <TableHead>Nombre</TableHead><TableHead>Código</TableHead><TableHead>Alérgenos</TableHead>
                <TableHead>Unidad</TableHead><TableHead className="w-16"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {prod.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-[#0E2A47]/40 py-8">Sin productos</TableCell></TableRow>}
                {prod.map((p) => (
                  <TableRow key={p.id} data-testid={`producto-${p.id}`}>
                    <TableCell className="font-medium text-[#0E2A47]">{p.nombre}</TableCell>
                    <TableCell>{p.codigo || "—"}</TableCell>
                    <TableCell>{alergBadges(p.alergenos)}</TableCell>
                    <TableCell>{p.unidad}</TableCell>
                    <TableCell>{canEdit && <Button size="sm" variant="ghost" onClick={() => abrir("productos", p)} data-testid={`editar-producto-${p.id}`}><Pencil size={14} /></Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog crear/editar */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white" data-testid="maestro-dialog">
          <DialogHeader><DialogTitle className="text-[#0E2A47]">
            {dialog?.editId ? "Editar" : "Nuevo"} {dialog?.tipo === "proveedores" ? "proveedor" : dialog?.tipo === "clientes" ? "cliente" : dialog?.tipo === "materias-primas" ? "materia prima" : "producto"}
          </DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Nombre">
              <Input value={form.nombre || ""} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="maestro-nombre" className="mt-1.5" />
            </Field>

            {dialog?.tipo === "proveedores" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="CIF"><Input value={form.cif || ""} onChange={(e) => setForm({ ...form, cif: e.target.value })} data-testid="maestro-cif" className="mt-1.5" /></Field>
                  <Field label="Contacto"><Input value={form.contacto || ""} onChange={(e) => setForm({ ...form, contacto: e.target.value })} data-testid="maestro-contacto" className="mt-1.5" /></Field>
                </div>
                <CertsEditor value={form.certificaciones} onChange={(v) => setForm({ ...form, certificaciones: v })} />
              </>
            )}

            {dialog?.tipo === "clientes" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="CIF"><Input value={form.cif || ""} onChange={(e) => setForm({ ...form, cif: e.target.value })} data-testid="cliente-cif" className="mt-1.5" /></Field>
                  <Field label="Persona de contacto"><Input value={form.persona_contacto || ""} onChange={(e) => setForm({ ...form, persona_contacto: e.target.value })} data-testid="cliente-contacto" className="mt-1.5" /></Field>
                  <Field label="Email"><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cliente-email" className="mt-1.5" /></Field>
                  <Field label="Teléfono"><Input value={form.telefono || ""} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="cliente-telefono" className="mt-1.5" /></Field>
                  <Field label="Población"><Input value={form.poblacion || ""} onChange={(e) => setForm({ ...form, poblacion: e.target.value })} data-testid="cliente-poblacion" className="mt-1.5" /></Field>
                  <Field label="País"><Input value={form.pais || ""} onChange={(e) => setForm({ ...form, pais: e.target.value })} data-testid="cliente-pais" className="mt-1.5" /></Field>
                </div>
                <Field label="Dirección"><Input value={form.direccion || ""} onChange={(e) => setForm({ ...form, direccion: e.target.value })} data-testid="cliente-direccion" className="mt-1.5" /></Field>
                <CertsEditor value={form.certificaciones} onChange={(v) => setForm({ ...form, certificaciones: v })} />
              </>
            )}

            {(dialog?.tipo === "materias-primas" || dialog?.tipo === "productos") && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Código"><Input value={form.codigo || ""} onChange={(e) => setForm({ ...form, codigo: e.target.value })} data-testid="maestro-codigo" className="mt-1.5" /></Field>
                  <Field label="Unidad">
                    <Select value={form.unidad || "kg"} onValueChange={(v) => setForm({ ...form, unidad: v })}>
                      <SelectTrigger data-testid="maestro-unidad" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="kg">kg</SelectItem><SelectItem value="g">g</SelectItem>
                        <SelectItem value="l">l</SelectItem><SelectItem value="ud">ud</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="Alérgenos (separados por comas)">
                  <Input value={form.alergenos || ""} onChange={(e) => setForm({ ...form, alergenos: e.target.value })} data-testid="maestro-alergenos" className="mt-1.5" placeholder="gluten, soja, sulfitos" />
                </Field>
              </>
            )}

            {dialog?.tipo === "productos" && (
              <div className="grid grid-cols-2 gap-4">
                <Field label="Rendimiento esperado (%)">
                  <Input type="number" value={form.rendimiento_esperado_pct ?? ""} onChange={(e) => setForm({ ...form, rendimiento_esperado_pct: e.target.value })} data-testid="maestro-rendimiento" className="mt-1.5" placeholder="p. ej. 90" />
                </Field>
                <Field label="Tolerancia (± %)">
                  <Input type="number" value={form.tolerancia_pct ?? ""} onChange={(e) => setForm({ ...form, tolerancia_pct: e.target.value })} data-testid="maestro-tolerancia" className="mt-1.5" placeholder="p. ej. 10" />
                </Field>
              </div>
            )}

            {dialog?.tipo === "materias-primas" && (
              <Field label="Proveedor">
                <Select value={form.proveedor_id || "none"} onValueChange={(v) => setForm({ ...form, proveedor_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="maestro-proveedor" className="mt-1.5"><SelectValue placeholder="Sin proveedor" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">Sin proveedor</SelectItem>
                    {prov.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
          </div>
          <DialogFooter><Button onClick={guardar} data-testid="maestro-submit" className="bg-[#0E2A47] hover:bg-[#163C65]">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {importEntidad && (
        <ImportDialog entidad={importEntidad} open={!!importEntidad}
          onClose={() => setImportEntidad(null)} onDone={load} />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">{label}</Label>
      {children}
    </div>
  );
}
